import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type MouseEvent,
} from "react"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useSupportsHover } from "@/hooks/use-supports-hover"
import { Place, PlacePlaceholder } from "@/types/place"
import { draggable as registerDraggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { cn } from "cn"

import { isPlacePlaceholder, relativeCanonicalUrl } from "@/lib/entity-utils"
import { placeHasFixedSchedule } from "@/lib/itinerary-utils"
import { selectIsSavedPlace } from "@/lib/storage/api/saved-places-api"
import {
  closeDetailSheet,
  EntitySheetTrackingContext,
  openEntitySheet,
  selectEntitySheetEntityRef,
  selectEntitySheetPriceQuoteRef,
} from "@/lib/storage/detail-sheet-slice"
import {
  clearDraggingItem,
  setDraggingItem,
} from "@/lib/storage/dragging-slice"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { selectIsPlaceOnItinerary } from "@/lib/storage/itinerary-slice"
import { selectIsPlaceHoveredOnMap } from "@/lib/storage/map-hover-slice"
import { RootState } from "@/lib/storage/redux-store"
import { extractFirstWord } from "@/lib/text-utils"
import IconHeart from "@/components/icons/heart"
import { PlaceAddTargetContext } from "./add-to-target-button"
import { AirbnbMark } from "./airbnb-mark"
import { EntityIcon } from "./entity-icon"
import { EntitySourceDot } from "./entity-source"
import { MessageAddedIcon } from "./message-added-icon"
import { useMessageTracksHoveredPlace } from "./message-card-layout"
import {
  messageEntityTriggerClassName,
  PlacePopoverContext,
  PlacePopoverCoordinatorContext,
  type PlacePopoverPayload,
} from "./place-popover-provider"
import { Link } from "./ui/link"
import { PopoverTrigger } from "./ui/popover"

interface Props {
  place: Place | PlacePlaceholder
  text: string
  messageIdForFeedback: number | undefined
  scheduleStartAt?: number // for matching schedule start_at timestamp for event places
  entitySheetTrackingContext: EntitySheetTrackingContext
  priceQuoteRef?: string
  // Shows Airbnb's mark in place of the place type's icon
  isAirbnb?: boolean
}

export const MessagePlaceButton = memo(function MessagePlaceButton({
  place,
  text,
  messageIdForFeedback,
  scheduleStartAt,
  entitySheetTrackingContext,
  priceQuoteRef,
  isAirbnb,
}: Props) {
  const dispatch = useAppDispatch()

  const isAdded = useAppSelector((state) =>
    selectIsPlaceOnItinerary(state, place)
  )
  const isSaved = useAppSelector((state) => selectIsSavedPlace(state, place))
  const isHoveredOnMap = useAppSelector((state) =>
    selectIsPlaceHoveredOnMap(state, place)
  )

  const selectIsSameSheetContextOpen = useCallback(
    (state: RootState) => {
      if (selectEntitySheetEntityRef(state) !== place.entity_ref) return false
      return selectEntitySheetPriceQuoteRef(state) === priceQuoteRef
    },
    [place.entity_ref, priceQuoteRef]
  )
  const isSameSheetContextOpen = useAppSelector(selectIsSameSheetContextOpen)
  const dragRef = useRef<HTMLAnchorElement>(null)

  const { firstWord, restOfText } = extractFirstWord(text)

  const isMD = useMediaQuery(MEDIA_QUERIES.md)
  const supportsHover = useSupportsHover()
  const handle = useContext(PlacePopoverContext)
  const popoverCoordinator = useContext(PlacePopoverCoordinatorContext)
  const isPlaceholder = isPlacePlaceholder(place)
  const addTarget = useContext(PlaceAddTargetContext)
  const syncMapHover = useMessageTracksHoveredPlace()
  const payload = useMemo<PlacePopoverPayload>(
    () => ({
      place,
      sideOffset: 0,
      messageIdForFeedback,
      scheduleStartAt,
      entitySheetTrackingContext,
      syncMapHover,
      addTarget,
    }),
    [
      place,
      messageIdForFeedback,
      scheduleStartAt,
      entitySheetTrackingContext,
      syncMapHover,
      addTarget,
    ]
  )

  useEffect(() => {
    if (isSameSheetContextOpen) {
      handle?.close()
    }
  }, [isSameSheetContextOpen, handle])

  useEffect(() => {
    const element = dragRef.current
    if (!element || isPlaceholder) return

    return registerDraggable({
      element,
      getInitialData: () => ({
        type: "message-place",
        entityRef: place.entity_ref,
      }),
      onDragStart: () => {
        const hasFixedSchedule = placeHasFixedSchedule(place)
        dispatch(
          setDraggingItem({
            type: "place",
            place,
            hasFixedSchedule: !!hasFixedSchedule,
          })
        )
      },
      onDrop: () => {
        dispatch(clearDraggingItem())
      },
    })
  }, [dispatch, place, isPlaceholder])

  const link = (
    <Link
      ref={dragRef}
      href={isPlaceholder ? "#" : relativeCanonicalUrl(place.canonical_url)}
      onClick={(
        e: MouseEvent<HTMLAnchorElement> & { preventBaseUIHandler?: () => void }
      ) => {
        e.preventDefault()
        e.preventBaseUIHandler?.()
        if (isPlaceholder) return

        handle?.close()
        if (isSameSheetContextOpen) {
          void dispatch(closeDetailSheet())
        } else {
          void dispatch(
            openEntitySheet({
              entity: place,
              messageId: messageIdForFeedback,
              scheduleStartAt,
              priceQuoteRef,
              entitySheetTrackingContext,
              addTarget,
            })
          )
        }
      }}
      className={cn(
        messageEntityTriggerClassName,
        isHoveredOnMap && "bg-foreground/5"
      )}
      onMouseEnter={() => {
        popoverCoordinator?.blurEntityTriggerFocusExcept(dragRef.current)
      }}
      data-id={place.entity_ref}
    >
      <span className="whitespace-nowrap">
        {isAirbnb ? (
          <AirbnbMark className="relative mt-[-.125em] mr-0.5 inline-block text-[1.25em]" />
        ) : (
          <EntityIcon
            place={place}
            className="relative mt-[-.125em] mr-0.5 inline-block text-[1.25em]"
          />
        )}
        {firstWord}
      </span>
      {restOfText ? <span>{restOfText}</span> : null}
      <EntitySourceDot entityRef={place.entity_ref} />
      {isAdded && <MessageAddedIcon />}
      {isSaved && (
        <span className="ml-0.5 whitespace-nowrap">
          <span>&#xfeff;</span>
          <IconHeart className="relative top-[-.0625em] inline-block fill-current text-saved" />
        </span>
      )}
    </Link>
  )

  if (handle && isMD && supportsHover) {
    return (
      <PopoverTrigger
        handle={handle}
        payload={payload}
        disabled={isSameSheetContextOpen}
        delay={150}
        closeDelay={300}
        nativeButton={false}
        render={link}
      />
    )
  }

  return link
})
